import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";
import SuperUsersPanel from "./SuperUsersPanel";
import PasswordResetRequestsPanel from "./PasswordResetRequestsPanel";

const UserAccess = () => {
  const [tab, setTab] = useState("requests");
  const [agents, setAgents] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [search, setSearch] = useState("");
  const [createdLogin, setCreatedLogin] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [agentRes, userRes, requestRes] = await Promise.all([
        axiosInstance.get("/agents"),
        axiosInstance.get("/auth/users").catch(() => ({ data: [] })),
        axiosInstance
          .get("/auth/password-reset-requests?status=pending")
          .catch(() => ({ data: { requests: [] } })),
      ]);
      setAgents(agentRes.data || []);
      setAppUsers(userRes.data || []);
      setPendingRequests(requestRes.data.requests || []);
    } catch (_) {
      toast.error("Failed to load user access data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const agentHasLogin = (agent) => {
    const id = String(agent._id);
    const email = String(agent.email || "")
      .trim()
      .toLowerCase();
    return appUsers.some((u) => {
      if (u.role !== "agent") return false;
      if (u.agentId && String(u.agentId) === id) return true;
      return Boolean(email) && String(u.email || "").toLowerCase() === email;
    });
  };
  const pendingAgentIds = new Set(
    pendingRequests
      .filter((r) => r.role === "agent")
      .map((r) => String(r.agentId || ""))
  );
  const pendingSuperuserIds = new Set(
    pendingRequests
      .filter((r) => r.role === "superuser")
      .map((r) => String(r.user || ""))
  );
  const pendingBrokerIds = new Set(
    pendingRequests
      .filter((r) => r.role === "broker")
      .map((r) => String(r.user || ""))
  );
  const pendingFinanceIds = new Set(
    pendingRequests
      .filter((r) => r.role === "finance")
      .map((r) => String(r.user || ""))
  );

  const filteredAgents = agents.filter((agent) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      String(agent.employeeNo || "").includes(q) ||
      `${agent.firstName || ""} ${agent.lastName || ""}`
        .toLowerCase()
        .includes(q) ||
      String(agent.email || "")
        .toLowerCase()
        .includes(q)
    );
  });

  const sendAgentLogin = async (agent) => {
    if (!agent.email) {
      toast.error("This agent has no email address on file.");
      return;
    }
    if (!window.confirm(`Email login credentials to ${agent.email}?`)) {
      return;
    }
    try {
      setSendingId(agent._id);
      const res = await axiosInstance.post("/auth/invite-agent", {
        agentId: agent._id,
      });
      toast.success(res.data.message);
      setCreatedLogin({
        name: `${agent.firstName} ${agent.lastName}`.trim(),
        username: res.data.username,
        password: res.data.password,
        emailSent: res.data.emailSent,
        emailError: res.data.emailError,
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send login email.");
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-blue-900 mb-2">User Access</h1>
        <p className="text-gray-600 mb-6">
          Create login accounts, email usernames and passwords, and resend
          passwords when a Super User, Broker, Agent, or Finance user requests
          one.
        </p>

        <div className="flex gap-4 mb-6 border-b">
          <button
            className={`px-4 py-2 font-medium ${
              tab === "requests"
                ? "text-blue-900 border-b-2 border-blue-900"
                : "text-gray-500"
            }`}
            onClick={() => setTab("requests")}
          >
            Password requests
            {pendingRequests.length > 0 ? ` (${pendingRequests.length})` : ""}
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              tab === "agents"
                ? "text-blue-900 border-b-2 border-blue-900"
                : "text-gray-500"
            }`}
            onClick={() => setTab("agents")}
          >
            Agents
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              tab === "superusers"
                ? "text-blue-900 border-b-2 border-blue-900"
                : "text-gray-500"
            }`}
            onClick={() => setTab("superusers")}
          >
            Super Users
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              tab === "brokers"
                ? "text-blue-900 border-b-2 border-blue-900"
                : "text-gray-500"
            }`}
            onClick={() => setTab("brokers")}
          >
            Brokers
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              tab === "finance"
                ? "text-blue-900 border-b-2 border-blue-900"
                : "text-gray-500"
            }`}
            onClick={() => setTab("finance")}
          >
            Finance
          </button>
        </div>

        {tab === "requests" ? (
          <PasswordResetRequestsPanel onResolved={load} />
        ) : tab === "superusers" ? (
          <SuperUsersPanel pendingUserIds={pendingSuperuserIds} onResolved={load} />
        ) : tab === "brokers" ? (
          <SuperUsersPanel
            roleType="broker"
            pendingUserIds={pendingBrokerIds}
            onResolved={load}
          />
        ) : tab === "finance" ? (
          <SuperUsersPanel
            roleType="finance"
            pendingUserIds={pendingFinanceIds}
            onResolved={load}
          />
        ) : (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-blue-900 mb-2">
              Agent logins
            </h2>
            <p className="text-gray-600 mb-4">
              Agents listed here cannot sign in until you click Send login email.
              That creates their username and password. Use those credentials
              under Agents on the login page.
            </p>
            {createdLogin && (
              <div
                className={`mb-4 p-4 rounded-md border ${
                  createdLogin.emailSent
                    ? "bg-green-50 border-green-200"
                    : "bg-yellow-50 border-yellow-300"
                }`}
              >
                <p className="font-semibold mb-2">
                  {createdLogin.emailSent
                    ? `Credentials emailed to ${createdLogin.name}.`
                    : createdLogin.emailError ||
                      "Email could not be sent. Copy these credentials and give them to the agent."}
                </p>
                <p>
                  <strong>Username:</strong> {createdLogin.username}
                </p>
                <p>
                  <strong>Password:</strong> {createdLogin.password}
                </p>
              </div>
            )}
            <input
              type="text"
              placeholder="Search by name, employee number, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full max-w-md mb-4 border rounded-md px-3 py-2"
            />
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-900"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-blue-900 text-white">
                      <th className="text-left px-3 py-2">Agent #</th>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-left px-3 py-2">Login</th>
                      <th className="text-left px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAgents.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-3 py-6 text-gray-500">
                          No agents found.
                        </td>
                      </tr>
                    ) : (
                      filteredAgents.map((agent) => (
                        <tr key={agent._id} className="border-b">
                          <td className="px-3 py-2">#{agent.employeeNo}</td>
                          <td className="px-3 py-2">
                            {agent.firstName} {agent.lastName}
                          </td>
                          <td className="px-3 py-2">{agent.email || "—"}</td>
                          <td className="px-3 py-2">
                            {pendingAgentIds.has(String(agent._id))
                              ? "Password requested"
                              : agentHasLogin(agent)
                                ? "Access sent"
                                : "No login yet"}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              disabled={sendingId === agent._id}
                              onClick={() => sendAgentLogin(agent)}
                              className="px-3 py-1 text-sm bg-blue-900 text-white rounded hover:bg-blue-800 disabled:opacity-60"
                            >
                              {sendingId === agent._id
                                ? "Sending..."
                                : pendingAgentIds.has(String(agent._id))
                                  ? "Resend password email"
                                  : "Send login email"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserAccess;
